import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Tag, Descriptions, Modal, Spin, Alert } from 'antd';
import {
  Link2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Unplug,
  ClipboardPaste,
  ArrowRight,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { CRM_CONNECT, CRM_CONNECTION, CRM_TEST, CRM_DISCONNECT } from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';

const { TextArea } = Input;

const ConnectCRM = () => {
  const { fetch, create } = useApiMethods();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [connection, setConnection] = useState(null);
  const [token, setToken] = useState('');
  const [testResult, setTestResult] = useState(null);

  const loadConnection = async () => {
    const res = await fetch(CRM_CONNECTION, {}, setLoading);
    if (res?.status === 'success') {
      setConnection(res.data);
    }
  };

  useEffect(() => {
    loadConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    if (!token.trim()) {
      ToastMsg('error', 'Please paste the connection token');
      return;
    }

    const res = await create(CRM_CONNECT, { token: token.trim() }, false, setConnecting);
    if (res?.status === 'success') {
      ToastMsg('success', res.message || 'CRM connected successfully');
      setToken('');
      setTestResult(null);
      loadConnection();
    } else {
      ToastMsg('error', res?.message || 'Failed to connect CRM');
    }
  };

  const handleTest = async () => {
    const res = await create(CRM_TEST, {}, false, setTesting);
    if (res?.status === 'success') {
      setTestResult(res.data);
      ToastMsg('success', 'Connection test passed');
      loadConnection();
    } else {
      setTestResult(res?.data || { is_healthy: false });
      ToastMsg('error', res?.message || 'Connection test failed');
    }
  };

  const handleDisconnect = () => {
    Modal.confirm({
      title: 'Disconnect CRM',
      content: 'Are you sure you want to disconnect from this CRM? You can reconnect later with a new token.',
      okText: 'Disconnect',
      okType: 'danger',
      onOk() {
        return new Promise(async (resolve, reject) => {
          try {
            const res = await create(CRM_DISCONNECT, {});
            if (res?.status === 'success') {
              ToastMsg('success', res.message || 'CRM disconnected');
              setConnection(null);
              setTestResult(null);
              resolve();
            } else {
              ToastMsg('error', res?.message || 'Failed to disconnect');
              reject();
            }
          } catch (err) {
            ToastMsg('error', 'Failed to disconnect');
            reject();
          }
        });
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  // Connected state
  if (connection && connection.is_connected) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Link2 size={22} />
            CRM Connection
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Your ERP is connected to TrustEdge CRM
          </p>
        </div>

        {/* Connection details */}
        <Card className="rounded-xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-base">{connection.org_name}</h3>
                <p className="text-sm text-muted-foreground">{connection.base_url}</p>
              </div>
            </div>
            <Tag color="success" className="text-sm px-3 py-1">Connected</Tag>
          </div>

          <Descriptions
            bordered
            size="small"
            column={1}
            className="mt-4"
            labelStyle={{ fontWeight: 500, width: 160 }}
          >
            <Descriptions.Item label="CRM Name">{connection.org_name}</Descriptions.Item>
            <Descriptions.Item label="Base URL">{connection.base_url}</Descriptions.Item>
            <Descriptions.Item label="API Key">{connection.api_key}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color="success">Connected</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Last Tested">
              {connection.last_tested_at
                ? new Date(connection.last_tested_at).toLocaleString()
                : 'Never'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Test result */}
        {testResult && (
          <Card
            className="rounded-xl shadow-sm"
            title={
              <span className="flex items-center gap-2">
                {testResult.is_healthy ? (
                  <CheckCircle size={16} className="text-green-600" />
                ) : (
                  <XCircle size={16} className="text-red-500" />
                )}
                Test Result
              </span>
            }
          >
            {testResult.is_healthy ? (
              <div className="space-y-3">
                <Alert type="success" message="Connection is healthy" showIcon />
                {testResult.preview && (
                  <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Pipeline Total">
                      {typeof testResult.preview.pipeline_total === 'number'
                        ? `$${testResult.preview.pipeline_total.toLocaleString()}`
                        : testResult.preview.pipeline_total}
                    </Descriptions.Item>
                    <Descriptions.Item label="Deal Count">
                      {testResult.preview.deal_count}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </div>
            ) : (
              <Alert type="error" message="Connection test failed" showIcon />
            )}
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            type="primary"
            icon={<RefreshCw size={14} />}
            onClick={handleTest}
            loading={testing}
            style={{ backgroundColor: '#4f46e5' }}
          >
            Test Connection
          </Button>
          <Button
            danger
            icon={<Unplug size={14} />}
            onClick={handleDisconnect}
            loading={disconnecting}
          >
            Disconnect
          </Button>
        </div>
      </div>
    );
  }

  // Not connected state
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Link2 size={22} />
          Connect Your CRM
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          Paste the connection token from your TrustEdge CRM to link it with this ERP platform
        </p>
      </div>

      <Card className="rounded-xl shadow-sm">
        <div className="space-y-5">
          {/* Instructions */}
          <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-100 rounded-lg p-4">
            <h4 className="font-medium text-indigo-900 mb-2">How to get your connection token:</h4>
            <ol className="text-sm text-indigo-800 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="font-semibold shrink-0">1.</span>
                <span>Go to TrustEdge CRM</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold shrink-0">2.</span>
                <span>Navigate to <strong>Settings</strong> <ArrowRight size={12} className="inline mx-1" /> <strong>ERP Integration</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold shrink-0">3.</span>
                <span>Click <strong>Generate Token</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold shrink-0">4.</span>
                <span>Copy the token and paste it below</span>
              </li>
            </ol>
          </div>

          {/* Token input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              <ClipboardPaste size={14} className="inline mr-1.5" />
              Connection Token
            </label>
            <TextArea
              rows={5}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your connection token here..."
              className="font-mono text-sm"
              disabled={connecting}
            />
          </div>

          <Button
            type="primary"
            size="large"
            icon={<Link2 size={16} />}
            onClick={handleConnect}
            loading={connecting}
            disabled={!token.trim()}
            style={{ backgroundColor: '#4f46e5' }}
            block
          >
            {connecting ? 'Connecting...' : 'Connect CRM'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ConnectCRM;
